import React from "react";
import { Eye, ShieldCheck, ScanFace, Lock, UserCheck, Flag } from "lucide-react";
import FeaturePage, { MockWindow } from "../features/FeaturePage";
import { siblingSolutions, DEMO_CTA } from "./links";

const ACCENT = "#E11D48";

const SIGNALS = [
  "Multiple faces", "Face missing", "Attention drift", "Looking away",
  "Looking down", "Audio coaching", "Tab switch", "Window blur", "Paste detected",
];

// ── Hero visual: integrity summary ──
function IntegrityVisual() {
  const flags = [
    { label: "Tab switch", n: 3, tone: "#E11D48" },
    { label: "Looking away", n: 2, tone: "#F59E0B" },
    { label: "Paste detected", n: 1, tone: "#E11D48" },
  ];
  return (
    <MockWindow label="INTEGRITY REPORT · SESSION" badge="6 flags" accent={ACCENT}>
      <div className="space-y-3">
        <div className="rounded-xl border p-3.5 flex items-center gap-3" style={{ borderColor: `${ACCENT}33`, background: `${ACCENT}0D` }}>
          <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${ACCENT}1A`, color: ACCENT }}>
            <ShieldCheck className="w-[18px] h-[18px]" />
          </div>
          <div>
            <div className="font-semibold text-sm text-[#0F172A]">Integrity score: review</div>
            <div className="text-[11px] text-muted-foreground">6 signals flagged across 18 minutes</div>
          </div>
        </div>
        <div className="space-y-2">
          {flags.map((f) => (
            <div key={f.label} className="flex items-center gap-3 rounded-xl border border-border p-2.5">
              <Flag className="w-3.5 h-3.5 flex-shrink-0" style={{ color: f.tone }} />
              <span className="text-xs text-[#0F172A] flex-1">{f.label}</span>
              <span className="font-mono text-[10px] font-bold px-1.5 py-0.5 rounded" style={{ background: `${f.tone}1A`, color: f.tone }}>×{f.n}</span>
            </div>
          ))}
        </div>
      </div>
    </MockWindow>
  );
}

// ── Row visual: the 9 signals grid ──
function SignalsVisual() {
  return (
    <MockWindow label="9 INTEGRITY SIGNALS" accent={ACCENT}>
      <div className="grid grid-cols-3 gap-2">
        {SIGNALS.map((s) => (
          <div key={s} className="rounded-xl border border-border p-2.5 text-center">
            <div className="w-6 h-6 rounded-md mx-auto mb-1.5 flex items-center justify-center" style={{ background: `${ACCENT}14`, color: ACCENT }}>
              <Flag className="w-3 h-3" />
            </div>
            <div className="font-mono text-[9px] text-[#0F172A] leading-tight">{s}</div>
          </div>
        ))}
      </div>
    </MockWindow>
  );
}

// ── Row visual: on-device gaze ──
function GazeVisual() {
  return (
    <MockWindow label="ON-DEVICE FACE & GAZE" badge="Private" accent={ACCENT}>
      <div className="space-y-3">
        <div className="rounded-xl border border-border p-4 flex items-center justify-center bg-secondary/30 h-28 relative">
          <ScanFace className="w-12 h-12" style={{ color: ACCENT }} />
          <div className="absolute top-2 left-2 font-mono text-[9px] text-muted-foreground">HEAD POSE · IRIS</div>
          <div className="absolute bottom-2 right-2 font-mono text-[9px]" style={{ color: ACCENT }}>tracking ✓</div>
        </div>
        <div className="flex items-center gap-2 rounded-xl border p-3" style={{ borderColor: `${ACCENT}33`, background: `${ACCENT}0D` }}>
          <Lock className="w-3.5 h-3.5 flex-shrink-0" style={{ color: ACCENT }} />
          <span className="text-[11px] text-muted-foreground leading-snug">Gaze runs in the candidate's browser — no video leaves the device for tracking.</span>
        </div>
      </div>
    </MockWindow>
  );
}

export default function ProctoringSolution() {
  return (
    <FeaturePage
      accent={ACCENT}
      signupRole="company"
      secondaryCta={DEMO_CTA}
      eyebrow="INTERVIEW INTEGRITY"
      title="Trust the results."
      titleAccent="Catch the cheating."
      subtitle="Nine integrity signals watch every AI interview — from multiple faces and gaze drift to tab-switching and pasted answers — so you know a strong score is genuinely the candidate's own work."
      heroChips={["9 integrity signals", "On-device face & gaze", "Flagged on every report"]}
      heroVisual={<IntegrityVisual />}
      rows={[
        {
          tag: "9 SIGNALS",
          title: "Nine ways we spot a compromised interview.",
          body: "Face and gaze analysis, environment checks, and behaviour signals run throughout the session. Each is timestamped and surfaced on the candidate's report, so nothing suspicious slips past a busy reviewer.",
          bullets: [
            "Multiple faces, face missing, attention & gaze drift",
            "Tab switch, window blur, and paste detection",
            "Audio coaching detection during answers",
          ],
          visual: <SignalsVisual />,
        },
        {
          tag: "PRIVATE BY DESIGN",
          title: "Face & gaze tracking that runs on-device.",
          body: "Head-pose and iris tracking happen in the candidate's own browser — the raw video isn't shipped off to a server to be analysed. You get the integrity signals without turning the interview into surveillance.",
          bullets: [
            "MediaPipe face & iris tracking in-browser",
            "Signals, not raw footage, drive the flags",
            "Respectful of candidate privacy",
          ],
          visual: <GazeVisual />,
          reverse: true,
        },
        {
          tag: "ON EVERY REPORT",
          title: "See integrity next to the score.",
          body: "Flags don't live in a separate dashboard you'll never open — they sit right on the candidate's evaluation, with counts and timing, so you can weigh a score in full context before advancing anyone.",
          bullets: [
            "Flags attached to each candidate result",
            "Counts and timestamps for context",
            "Decide with the full picture in view",
          ],
          visual: <IntegrityVisual />,
        },
      ]}
      steps={[
        { title: "Candidate interviews", body: "Integrity monitoring runs automatically during every AI interview." },
        { title: "Signals are captured", body: "Face, gaze, environment, and behaviour events are timestamped as they happen." },
        { title: "Review with context", body: "Flags appear on the candidate's report so you can judge the score fairly." },
      ]}
      useCases={[
        { icon: UserCheck, persona: "Remote hiring", body: "When you can't be in the room, integrity signals give you confidence in unproctored, on-demand interviews." },
        { icon: ShieldCheck, persona: "High-stakes roles", body: "For roles where skills must be real, know a top score wasn't coached or pasted in." },
        { icon: Eye, persona: "High-volume screening", body: "Automatically flag the sessions worth a second look instead of re-watching everything." },
      ]}
      faqs={[
        { q: "What exactly is monitored?", a: "Nine signals: multiple faces, face missing, attention drift, looking away, looking down, audio coaching, tab switching, window blur, and pasted content — each timestamped during the session." },
        { q: "Is the candidate's video sent to a server for tracking?", a: "No. Face and gaze analysis run on-device in the candidate's browser using MediaPipe, so the raw video isn't uploaded just to be analysed. The interview recording is a separate, opt-in feature." },
        { q: "Does a flag mean the candidate cheated?", a: "Not on its own. Flags are signals with counts and timing that you review in context alongside the score — they inform your judgement rather than auto-rejecting anyone." },
        { q: "Where do I see the flags?", a: "Directly on each candidate's evaluation, next to their score and per-question feedback, so integrity is part of the same review." },
        { q: "Does it work on any device?", a: "It runs in a standard browser with a webcam — no plugin or install for the candidate." },
      ]}
      ctaTitle="Know the score is real."
      ctaSubtitle="Start free, or book a demo to see integrity reporting in action."
      related={siblingSolutions("proctoring")}
      seoTitle="AI Interview Proctoring — 9 Integrity Signals | VetraHub"
      seoDescription="Nine on-device integrity signals watch every AI interview — multiple faces, gaze drift, tab switching, paste detection and more — flagged right on the candidate's report."
    />
  );
}
